import { KIND } from 'baseui/button'
import { useTranslation } from 'react-i18next'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import SDNotification from '@/shared/components/SDComponentsNewDesign/SDNotification/SDNotification'
import { escapeHtml } from '@/shared/tools/stringFunctions'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import OnboardingSteps, { Step } from './OnboardingSteps'
import RegistrationCode, { RegistrationCodeProps } from './RegistrationCode'
import {
  StyledActions,
  StyledBadge,
  StyledCardHeader,
  StyledCardTitle,
  StyledCodeCard,
  StyledInfo,
  StyledScript,
  StyledSentBox,
  StyledSentDetails,
  StyledSentTitle,
} from './styled-components'

const COPY = 'subscriptionForm:fidelity_maximizer_module.code_onboarding'

interface CardAction {
  hidden: boolean
  label: string
  disabled: boolean
  loading: boolean
  onClick: () => void
}

interface CodeOnboardingCardProps {
  hidden: boolean
  steps: Step[]
  sentBox: { hidden: boolean; title: string; details: string }
  script: string
  info: string
  code: RegistrationCodeProps
  outcome: { hidden: boolean; type: string; message: string; detail: string }
  primaryAction: CardAction
  resendAction: CardAction
}

const CodeOnboardingCard = ({
  hidden,
  steps,
  sentBox,
  script,
  info,
  code,
  outcome,
  primaryAction,
  resendAction,
}: CodeOnboardingCardProps) => {
  const { t } = useTranslation()
  const isPulse = usePulseScope()

  if (hidden) {
    return null
  }

  return (
    <StyledCodeCard $pulse={isPulse}>
      <StyledCardHeader $pulse={isPulse}>
        <StyledCardTitle $pulse={isPulse}>{t(`${COPY}.card_title`)}</StyledCardTitle>
        <StyledBadge $pulse={isPulse}>{t(`${COPY}.badge`)}</StyledBadge>
      </StyledCardHeader>

      <OnboardingSteps steps={steps} />

      <StyledSentBox hidden={sentBox.hidden} $pulse={isPulse}>
        <StyledSentTitle $pulse={isPulse}>{sentBox.title}</StyledSentTitle>
        <StyledSentDetails $pulse={isPulse}>{sentBox.details}</StyledSentDetails>
      </StyledSentBox>

      <StyledScript $pulse={isPulse} dangerouslySetInnerHTML={{ __html: script }} />

      <RegistrationCode {...code} />

      <SDNotification
        hidden={outcome.hidden}
        type={outcome.type}
        primaryText={`<strong>${escapeHtml(outcome.message)}</strong>${
          outcome.detail ? `<br />${outcome.detail}` : ''
        }`}
      />

      <StyledActions>
        {/* @ts-ignore */}
        <SDButton
          hidden={primaryAction.hidden}
          block={true}
          disabled={primaryAction.disabled}
          isLoading={primaryAction.loading}
          onClick={primaryAction.onClick}
        >
          {primaryAction.label}
        </SDButton>
        {/* @ts-ignore */}
        <SDButton
          hidden={resendAction.hidden}
          block={true}
          kind={KIND.secondary}
          disabled={resendAction.disabled}
          isLoading={resendAction.loading}
          onClick={resendAction.onClick}
        >
          {resendAction.label}
        </SDButton>
      </StyledActions>

      <StyledInfo $pulse={isPulse}>{info}</StyledInfo>
    </StyledCodeCard>
  )
}

export default CodeOnboardingCard
